import type { PricingTier } from '../components/data/mock-pricing';

/**
 * PLAT-6791: which memberships / All Access packs a signed-in member can use
 * for one activity. Mirrors the Customer Dashboard
 * (`apps/web/.../EventPurchaseModal.tsx` `isUsableOwnedSubscriptionPrice` and
 * `getOwnedApplicableSubscriptionPrice`) so the embed and the dashboard reach
 * the same verdict from the same payment-options payload.
 *
 * An All Access pack is not a product flag: the activity has a $0 pricing tier
 * for the product (`subscriptionPrices[].price === 0`). A positive owned price
 * is a member discount on the drop-in instead.
 */

export interface OwnedPriceOption {
  productId: string;
  price: number;
  ownsProduct?: boolean | null;
  isWithinBillingPeriod?: boolean | null;
  isDeferredMembershipAccess?: boolean | null;
  deferredMembershipPurchaseId?: string | null;
  deferredChargeSettings?: PricingTier['deferredChargeSettings'] | null;
}

export interface SubscriptionStatusLike {
  productId: string;
  isActive: boolean;
  isPaused?: boolean | null;
}

export interface OwnedAccessInput {
  payment?: OwnedPriceOption | null;
  subscriptionPrices?: OwnedPriceOption[] | null;
  allSubscriptionStatuses?: SubscriptionStatusLike[] | null;
}

export interface OwnedAccess {
  /** Usable owned products with a $0 tier — book free with `accessedWithProductId`. */
  freeProductIds: string[];
  /** Every usable owned product for this activity; never offered for purchase. */
  ownedProductIds: string[];
  /**
   * Member price for the drop-in when an owned membership discounts it (and no
   * free access applies). Undefined when the member pays the normal price.
   */
  memberDropInPrice?: number;
  /** Reservation metadata belongs to the beneficiary whose context was loaded. */
  deferredMemberships?: Record<string, Pick<PricingTier, 'deferredMembershipPurchaseId' | 'deferredChargeSettings'>>;
}

/** Dashboard parity: owned, inside its billing period, and active/unpaused. */
export function isUsableOwnedPrice(
  input: OwnedAccessInput,
  option: OwnedPriceOption,
): boolean {
  if (!option.ownsProduct) return false;
  if (option.isWithinBillingPeriod === false) return false;
  // The server has validated this pending reservation for the concrete target;
  // it does not have an active Stripe subscription yet.
  if (option.isDeferredMembershipAccess && option.deferredMembershipPurchaseId) return true;
  const statuses = input.allSubscriptionStatuses ?? [];
  // No statuses at all (e.g. a comped membership with no Stripe subscription)
  // passes, exactly as the dashboard does.
  if (!statuses.length) return true;
  return statuses.some(
    (s) => s.productId === option.productId && s.isActive && !s.isPaused,
  );
}

export function ownedAccess(input: OwnedAccessInput | null | undefined): OwnedAccess {
  if (!input) return { freeProductIds: [], ownedProductIds: [] };
  const usable = (input.subscriptionPrices ?? []).filter((o) =>
    isUsableOwnedPrice(input, o),
  );
  const freeProductIds = [
    ...new Set(usable.filter((o) => o.price === 0).map((o) => o.productId)),
  ];
  const ownedProductIds = [...new Set(usable.map((o) => o.productId))];
  const deferredMemberships = Object.fromEntries(
    usable.filter((o) => o.deferredMembershipPurchaseId).map((o) => [o.productId, {
      deferredMembershipPurchaseId: o.deferredMembershipPurchaseId!,
      deferredChargeSettings: o.deferredChargeSettings ?? undefined,
    }]),
  );
  const cheapestPaid = usable
    .filter((o) => o.price > 0)
    .reduce<OwnedPriceOption | undefined>(
      (best, o) => (!best || o.price < best.price ? o : best),
      undefined,
    );
  // The server already folds the member's best owned price into `payment`;
  // take it from there only when that price really comes from an owned
  // membership, never from a price the member merely could buy.
  const payment = input.payment;
  const memberDropInPrice =
    !freeProductIds.length &&
    cheapestPaid &&
    payment &&
    payment.productId === cheapestPaid.productId
      ? payment.price
      : undefined;
  return {
    freeProductIds,
    ownedProductIds,
    ...(Object.keys(deferredMemberships).length ? { deferredMemberships } : {}),
    ...(memberDropInPrice === undefined ? {} : { memberDropInPrice }),
  };
}

/** Buyer-only drop-in at the member's discounted price. */
export const MEMBER_DROPIN_TIER_ID = 'member-dropin';

/** Guest-purchasable copy of a tier the buyer holds as All Access. */
export const BUY_PREFIX = 'buy:';

/**
 * Whether one attendee may pick `tier`. `isBuyer` is true only for the
 * signed-in member booking their own seat.
 */
export function tierVisibleTo(
  tier: PricingTier,
  isBuyer: boolean,
  freeProductIds: readonly string[],
): boolean {
  switch (tier.audience) {
    case 'buyer':
      return isBuyer;
    case 'others':
      return !isBuyer;
    case 'owner':
      return isBuyer && freeProductIds.includes(tier.id);
    default:
      return true;
  }
}

/**
 * Re-shape an activity's tiers for a signed-in member (idempotent):
 * - an owned All Access tier stays under its product id at $0 for the buyer,
 *   and a copy at the shop price (`buy:<id>`) stays purchasable for guests;
 * - a membership the member owns at a positive price is hidden from them;
 * - a member-priced drop-in is added for the buyer and the regular drop-in is
 *   left to guests.
 */
export function memberTiers(
  tiers: readonly PricingTier[],
  access: {
    freeProductIds: readonly string[];
    ownedProductIds: readonly string[];
    memberDropInPrice: number | null | undefined;
    deferredMemberships?: OwnedAccess['deferredMemberships'];
  },
): PricingTier[] {
  const base = tiers.filter(
    (t) => t.id !== MEMBER_DROPIN_TIER_ID && !t.id.startsWith(BUY_PREFIX),
  );
  const out: PricingTier[] = [];
  const free = new Set(access.freeProductIds);
  const owned = new Set(access.ownedProductIds);
  const dropIn = base.find((t) => t.id === 'dropin');
  const memberDropIn =
    !free.size &&
    dropIn &&
    typeof access.memberDropInPrice === 'number' &&
    access.memberDropInPrice < dropIn.price;
  const buyCopies = new Map(
    tiers.filter((t) => t.id.startsWith(BUY_PREFIX)).map((t) => [t.id, t]),
  );
  for (const t of base) {
    if (free.has(t.id)) {
      const buyId = `${BUY_PREFIX}${t.id}`;
      // A pending reservation is the buyer's access; carry it so booking
      // goes through the reservation, as family tiers do.
      out.push({
        ...t,
        price: 0,
        audience: 'buyer',
        ...access.deferredMemberships?.[t.id],
      });
      // Guests can still buy it. Its shop price comes from the catalog
      // (`enrichTierNames`), which also drops the copy when the shop can't
      // price it; an `owner` tier was already found unpriceable.
      if (t.audience !== 'owner') {
        const purchasable: PricingTier = { ...(buyCopies.get(buyId) ?? t), id: buyId, audience: 'others' };
        delete purchasable.deferredMembershipPurchaseId;
        delete purchasable.isDeferredMembershipAccess;
        out.push(purchasable);
      }
    } else if (owned.has(t.id)) {
      out.push({ ...t, audience: 'others' });
    } else if (t.id === 'dropin' && memberDropIn) {
      out.push({
        ...t,
        id: MEMBER_DROPIN_TIER_ID,
        sub: 'Member price',
        price: access.memberDropInPrice as number,
        perSession: access.memberDropInPrice as number,
        audience: 'buyer',
      });
      out.push({ ...t, audience: 'others' });
    } else {
      out.push(t);
    }
  }
  return out;
}

/** Prefix of tiers synthesized for one family member (see {@link withFamilyAccess}). */
const familyPrefix = (familyUserId: number) => `fam:${familyUserId}:`;

/**
 * PLAT-6805: the product a family member's own access tier stands for, or ""
 * when `tierId` is not their free All Access tier.
 */
export function familyAccessProductId(
  tierId: string,
  familyUserId: number,
  freeProductIds: readonly string[],
): string {
  const prefix = familyPrefix(familyUserId);
  if (!tierId.startsWith(prefix)) return '';
  const productId = tierId.slice(prefix.length);
  return freeProductIds.includes(productId) ? productId : '';
}

/**
 * Whether one attendee may pick `tier`. Family attendees see the guest view
 * of the activity's tiers, minus what they own, plus tiers built from their
 * own memberships ({@link withFamilyAccess}).
 */
export function tierVisibleToAttendee(
  tier: PricingTier,
  attendee: { isBuyer: boolean; familyUserId?: number },
  buyerFreeProductIds: readonly string[],
): boolean {
  const familyUserId = attendee.isBuyer ? undefined : attendee.familyUserId;
  if (tier.forFamilyUserId !== undefined) {
    return familyUserId === tier.forFamilyUserId;
  }
  if (familyUserId && tier.hideForFamilyUserIds?.includes(familyUserId)) {
    return false;
  }
  return tierVisibleTo(tier, attendee.isBuyer, buyerFreeProductIds);
}

/**
 * PLAT-6805: shape the tiers for one family member's own memberships, the way
 * {@link memberTiers} does for the signed-in buyer (idempotent; call again
 * whenever either changes):
 * - their All Access product gets a $0 tier only they see, and the purchasable
 *   copy of that product is hidden from them;
 * - memberships they already own are hidden from them;
 * - a member drop-in price replaces the drop-in for them.
 * Nothing changes for any other attendee.
 */
export function withFamilyAccess(
  tiers: readonly PricingTier[],
  familyUserId: number,
  access: {
    freeProductIds: readonly string[];
    ownedProductIds: readonly string[];
    memberDropInPrice: number | null | undefined;
    deferredMemberships?: OwnedAccess['deferredMemberships'];
  },
  buyerFreeProductIds: readonly string[],
): PricingTier[] {
  const prefix = familyPrefix(familyUserId);
  const base = tiers
    .filter((t) => t.forFamilyUserId !== familyUserId)
    .map((t): PricingTier => {
      const hidden = t.hideForFamilyUserIds;
      if (!hidden?.includes(familyUserId)) return t;
      const rest = hidden.filter((id) => id !== familyUserId);
      const tier: PricingTier = { ...t };
      delete tier.hideForFamilyUserIds;
      return rest.length ? { ...tier, hideForFamilyUserIds: rest } : tier;
    });
  const guestView = (t: PricingTier) =>
    tierVisibleTo(t, false, buyerFreeProductIds);
  // The tier a guest would buy this product through ("buy:x" when the buyer
  // holds it free, else the product's own tier).
  const guestTierFor = (productId: string) =>
    base.find(
      (t) =>
        t.forFamilyUserId === undefined &&
        guestView(t) &&
        (t.id === productId || t.id === `${BUY_PREFIX}${productId}`),
    );
  const hide = new Set<string>();
  const added: PricingTier[] = [];
  const free = new Set(access.freeProductIds);
  for (const productId of free) {
    const source =
      guestTierFor(productId) ?? base.find((t) => t.id === productId);
    if (!source) continue;
    if (guestView(source)) hide.add(source.id);
    added.push({
      ...familyTier(source, `${prefix}${productId}`, familyUserId, 0),
      ...access.deferredMemberships?.[productId],
    });
  }
  for (const productId of access.ownedProductIds) {
    const source = guestTierFor(productId);
    if (source) hide.add(source.id);
  }
  const dropIn = base.find(
    (t) => t.id === 'dropin' && t.forFamilyUserId === undefined && guestView(t),
  );
  if (
    !free.size &&
    dropIn &&
    typeof access.memberDropInPrice === 'number' &&
    access.memberDropInPrice < dropIn.price
  ) {
    hide.add(dropIn.id);
    added.push({
      ...familyTier(dropIn, `${prefix}member-dropin`, familyUserId, access.memberDropInPrice),
      sub: 'Member price',
      perSession: access.memberDropInPrice,
    });
  }
  return [
    ...base.map((t) =>
      hide.has(t.id)
        ? {
            ...t,
            hideForFamilyUserIds: [...(t.hideForFamilyUserIds ?? []), familyUserId],
          }
        : t,
    ),
    ...added,
  ];
}

function familyTier(
  source: PricingTier,
  id: string,
  familyUserId: number,
  price: number,
): PricingTier {
  const tier: PricingTier = { ...source, id, price, forFamilyUserId: familyUserId };
  delete tier.audience;
  delete tier.hideForFamilyUserIds;
  delete tier.deferredMembershipPurchaseId;
  delete tier.deferredChargeSettings;
  delete tier.isDeferredMembershipAccess;
  return tier;
}
